import { FolderOpen } from 'lucide-react';
import type { HolderSwitcherOption } from '@/components/HolderSwitcher';
import type { TodoWorkspaceSummary } from '@/lib/api';

interface OtherWorkspacesBannerProps {
  /** Per-workspace visible-list counts from the workspace-summary endpoint. */
  summaries: TodoWorkspaceSummary[];
  /** The workspace currently being viewed. */
  selectedHolderId: string | null;
  /** Accessible workspaces — used to resolve display names. */
  options: HolderSwitcherOption[];
  /** Switch the active workspace. */
  onSwitch: (holderId: string) => void;
}

interface OtherWorkspace {
  holderId: string;
  name: string;
  listCount: number;
}

function resolveName(summary: TodoWorkspaceSummary, options: HolderSwitcherOption[]): string {
  const match = options.find((o) => o.holderId === summary.holderId);
  if (match) return match.displayName;
  // Fallback when the switcher hasn't resolved a name (e.g. mid-load).
  return summary.holderType === 'org' ? 'Organization workspace' : 'Personal';
}

/**
 * Informs the user when they have todo lists in workspaces OTHER than
 * the one currently selected, with one-click switching. Without this,
 * switching workspace looks like the lists were deleted — the exact
 * confusion this banner exists to prevent.
 *
 * Renders nothing when there are no other workspaces with visible lists.
 */
export function OtherWorkspacesBanner({
  summaries,
  selectedHolderId,
  options,
  onSwitch,
}: OtherWorkspacesBannerProps) {
  const others: OtherWorkspace[] = summaries
    .filter((s) => s.holderId !== selectedHolderId && s.listCount > 0)
    .map((s) => ({ holderId: s.holderId, name: resolveName(s, options), listCount: s.listCount }));

  if (others.length === 0) return null;

  const totalElsewhere = others.reduce((sum, w) => sum + w.listCount, 0);
  const listWord = totalElsewhere === 1 ? 'list' : 'lists';
  const spaceWord = others.length === 1 ? 'workspace' : 'workspaces';

  return (
    <section
      className="border-l-4 border-primary bg-primary/5 rounded-r-md p-4"
      aria-label="Lists in other workspaces"
      data-testid="todo-other-workspaces-banner"
    >
      <p className="text-sm flex items-center gap-2">
        <FolderOpen className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
        <span>
          You also have <strong>{totalElsewhere}</strong> {listWord} in {others.length} other{' '}
          {spaceWord}. Switch to view:
        </span>
      </p>
      <div className="flex flex-wrap gap-2 mt-2">
        {others.map((w) => (
          <button
            key={w.holderId}
            type="button"
            onClick={() => onSwitch(w.holderId)}
            data-testid={`todo-other-workspace-${w.holderId}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-primary/10 transition-colors"
          >
            <span>{w.name}</span>
            <span className="text-muted-foreground">
              {w.listCount} {w.listCount === 1 ? 'list' : 'lists'}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
